import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons, type IconName } from '@/assets/icons/icons';
import imessageLogo from '@/assets/images/channel-dashboard/imessage.png';
import whatsappLogo from '@/assets/images/channel-dashboard/whatsapp.png';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Box, Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import TextArea from '@/components/ui/TextArea';
import {
  type Selection,
  useIntegrationGrid,
} from '@/features/integrations/add-integrations.hooks';
import {
  SUGGESTED_SELECTION,
  type IntegrationState,
} from '@/features/integrations/integrations.types';
import { useConnectFromLink } from '@/features/integrations/use-connect-from-link';
import { useDeleteMCPServerMutation } from '@/features/mcp/mcp.query';
import { openChannelsBetaFeedback } from '@/features/msg-channels/common/msg-channels.feedback';
import {
  MESSAGING_CHANNEL_TYPES,
  type MessagingChannelType,
  MessagingChannelTypes,
} from '@/features/msg-channels/common/msg-channels.types';
import { IMessageChannelConnectModal } from '@/features/msg-channels/imessage/IMessageChannelConnectModal';
import { WhatsAppChannelConnectModal } from '@/features/msg-channels/whatsapp/WhatsAppChannelConnectModal';
import { useChatPromptFields } from '@/features/settings/sub-settings/chat-prompt-fields';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { useHashRoute } from '@/hooks/useHashRoute';
import { isConnectableDeviceToolkit } from '@/integrations/device-integrations';
import { getBrandIcon } from '@/integrations/integration-icons';
import { integrationsStore } from '@/integrations/integrations.store';
import {
  connectIntegration,
  hasIntegrationAccess,
} from '@/integrations/integrations.utils';
import { useIntegrationActions } from '@/integrations/useIntegrationActions';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { logoutUser } from '@/utils/auth';
import { inBrowser, inDesktop } from '@/utils/capabilities';
import { SUPPORT_EMAIL } from '@/utils/constants';
import { DOWNLOAD_URL } from '@/utils/deepLink';
import { handleExternalUrlClick } from '@/utils/platform';

import { confirmIntegrationDisconnect } from './channel-dashboard.actions';
import {
  useChannelConnectionSetup,
  useChannelConnections,
  useChannelConnectionState,
} from './channel-dashboard.hooks';
import {
  CHANNEL_SURFACE_CLASS_NAME,
  ChannelBadge,
  ChannelPageHeader,
  ChannelRouteSheet,
  ChannelSearchInput,
  ChannelSurface,
  DashboardBackLink,
  HomeCard,
  SOFT_INSET_CLASS_NAME,
} from './channel-dashboard.parts';
import { readViaChannel, rememberViaChannel } from './channel-via';

export const CHANNEL_DEFINITIONS = {
  [MessagingChannelTypes.IMESSAGE]: {
    name: 'iMessage',
    shortName: 'iM',
    description: 'Use Littlebird in Messages',
    openLabel: 'Get started',
    accentClassName: 'bg-blue-500 text-white',
    logoUrl: imessageLogo,
    logoClassName: 'scale-[1.2]',
    surfaceClassName:
      'bg-[linear-gradient(145deg,color-mix(in_srgb,var(--surface-default)_72%,transparent),color-mix(in_srgb,var(--brand-jordy-blue)_30%,transparent))]',
  },
  [MessagingChannelTypes.WHATSAPP_BUSINESS]: {
    name: 'WhatsApp',
    shortName: 'WA',
    description: 'Use Littlebird in WhatsApp',
    openLabel: 'Get started',
    accentClassName: 'bg-green-500 text-white',
    logoUrl: whatsappLogo,
    logoBackgroundClassName: 'bg-[#25D366]',
    logoClassName: 'scale-[1.15]',
    surfaceClassName:
      'bg-[linear-gradient(145deg,color-mix(in_srgb,var(--surface-default)_72%,transparent),color-mix(in_srgb,var(--brand-emerald-green)_26%,transparent))]',
  },
} satisfies Record<
  MessagingChannelType,
  {
    name: string;
    shortName: string;
    description: string;
    openLabel: string;
    accentClassName: string;
    logoUrl: string;
    logoBackgroundClassName?: string;
    logoClassName?: string;
    surfaceClassName: string;
  }
>;

const getChannelManageModal = (channelType: MessagingChannelType) => {
  switch (channelType) {
    case MessagingChannelTypes.IMESSAGE:
      return IMessageChannelConnectModal;
    case MessagingChannelTypes.WHATSAPP_BUSINESS:
      return WhatsAppChannelConnectModal;
  }
};

const SHOWCASED_INTEGRATIONS = [
  { name: 'Gmail', Icon: Icons.gmail },
  { name: 'Google Calendar', Icon: Icons.googleCalendar },
  { name: 'Google Drive', Icon: Icons.googleDrive },
];

const DASHBOARD_STATUS_DESCRIPTION = 'Ready when you are';
const CHANNEL_NOISE_TEXTURE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")";

const UserInitialsAvatar = ({
  label,
  className,
}: {
  label: string;
  className?: string;
}) => (
  <XStack
    center
    aria-hidden
    className={cn(
      'shrink-0 rounded-full border border-[var(--glass-edge)] bg-background-muted text-sm font-medium text-foreground',
      className
    )}
  >
    {label
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()}
  </XStack>
);

export const ChannelDashboardShell = observer(() => {
  const user = userStore.user;
  useEffect(() => {
    rememberViaChannel();
  }, []);

  return (
    <YStack className="glass-theme relative h-dvh overflow-y-auto bg-[radial-gradient(circle_at_12%_8%,var(--background-subtle),transparent_38%),linear-gradient(155deg,var(--background),var(--background-muted))] text-foreground">
      <Box className="relative z-10 mx-auto flex min-h-full w-full max-w-4xl flex-1 flex-col px-6 py-6 sm:px-10 sm:py-10">
        <XStack className="mb-8 items-center justify-between">
          <Link
            to="/channels"
            aria-label="Littlebird channel dashboard"
            className="flex items-center rounded-sm focus-ring"
          >
            <AppLogo size={32} />
          </Link>

          <XStack className="min-w-0 items-center gap-3">
            {user?.email && (
              <span className="truncate text-xs text-foreground-muted">
                Signed in as {user.email}
              </span>
            )}
            <Link
              to="/channels/settings"
              aria-label="Open settings"
              title="Settings"
              className="rounded-full focus-ring"
            >
              <UserInitialsAvatar
                label={user?.name?.trim() || user?.email || 'User'}
                className="size-10"
              />
            </Link>
          </XStack>
        </XStack>
        <ChannelOverview />
        <Outlet />
      </Box>
      <Box
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[5] opacity-[0.08] mix-blend-soft-light"
        style={{ backgroundImage: CHANNEL_NOISE_TEXTURE }}
      />
    </YStack>
  );
});

const MessagingChannelsCard = ({
  channels,
}: {
  channels: MessagingChannelType[];
}) => {
  const navigate = useNavigate();

  return (
    <HomeCard.Root
      onClick={() => void navigate({ to: '/channels/connect' })}
      className="col-span-2 min-h-56 justify-between gap-5 bg-[linear-gradient(120deg,color-mix(in_srgb,var(--brand-jordy-blue)_24%,var(--surface-default)),color-mix(in_srgb,var(--brand-emerald-green)_20%,var(--surface-default)))]"
    >
      <XStack className="gap-2">
        {channels.map((channelType) => {
          const channel = CHANNEL_DEFINITIONS[channelType];
          return (
            <Box key={channelType}>
              <ChannelBadge channel={channel} />
              <span className="sr-only">{channel.name}</span>
            </Box>
          );
        })}
      </XStack>

      <YStack className="gap-4">
        <HomeCard.Content>
          <HomeCard.Title>Messaging</HomeCard.Title>
          <HomeCard.Description>
            Access Littlebird from iMessage or WhatsApp
          </HomeCard.Description>
        </HomeCard.Content>
        <span
          className={cn(
            SOFT_INSET_CLASS_NAME,
            'grid h-11 w-full place-items-center rounded-full border-[var(--glass-edge-strongest)] px-6 text-[15px] font-medium text-foreground shadow-sm'
          )}
        >
          Manage messaging
        </span>
      </YStack>
    </HomeCard.Root>
  );
};

const DesktopDownloadCard = () => {
  const openDownloadPage = () => handleExternalUrlClick(DOWNLOAD_URL);

  return (
    <HomeCard.Root
      onClick={openDownloadPage}
      className="pr-12 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--surface-default)_76%,transparent),color-mix(in_srgb,var(--glass-tint-deep)_18%,transparent))]"
    >
      <XStack className="items-start gap-3">
        <HomeCard.Icon>
          <AppLogo size={20} />
        </HomeCard.Icon>
        <HomeCard.Content className="min-w-0">
          <HomeCard.Title>Littlebird for desktop</HomeCard.Title>
          <HomeCard.Description>
            Capture context, record meetings, and chat from your computer
          </HomeCard.Description>
        </HomeCard.Content>
      </XStack>
      <Icons.arrowUpRight className="absolute right-5 top-5 size-4 text-foreground-muted" />
    </HomeCard.Root>
  );
};

const ChannelOverview = observer(() => {
  useChannelConnections();
  const navigate = useNavigate();
  const firstName = userStore.user?.name?.trim().split(/\s+/)[0] || 'there';

  return (
    <YStack className="min-h-0 flex-1 gap-6">
      <YStack className="flex-1 items-center justify-center gap-2 text-center">
        <h1 className="text-[2.5rem] font-light leading-none tracking-[-0.04em] text-foreground sm:text-5xl">
          Hello, {firstName}
        </h1>
        <p className="text-base text-foreground-subtle">
          {DASHBOARD_STATUS_DESCRIPTION}
        </p>
      </YStack>

      <YStack className="gap-4">
        <DesktopDownloadCard />
        <Grid className="grid-cols-2 gap-3">
          <MessagingChannelsCard channels={MESSAGING_CHANNEL_TYPES} />
        </Grid>
        <Grid className="grid-cols-2 gap-4">
          <HomeCard.Root
            onClick={() => void navigate({ to: '/channels/integrations' })}
            className="min-h-48 justify-between bg-[linear-gradient(145deg,color-mix(in_srgb,var(--surface-default)_74%,transparent),color-mix(in_srgb,var(--glass-tint-deep)_18%,transparent))]"
          >
            <XStack className="-space-x-1.5">
              {SHOWCASED_INTEGRATIONS.map(({ name, Icon }) => (
                <HomeCard.Icon key={name} className="bg-white shadow-sm">
                  <Icon className="size-6" />
                </HomeCard.Icon>
              ))}
            </XStack>
            <HomeCard.Content>
              <HomeCard.Title>Integrations</HomeCard.Title>
              <HomeCard.Description>
                Connect Gmail, Calendar, Drive, and more
              </HomeCard.Description>
            </HomeCard.Content>
          </HomeCard.Root>

          <HomeCard.Root
            onClick={() => void navigate({ to: '/channels/routines' })}
            className="min-h-48 justify-between bg-[radial-gradient(circle_at_85%_10%,color-mix(in_srgb,var(--surface-default)_74%,transparent),transparent_32%),linear-gradient(145deg,color-mix(in_srgb,var(--brand-emerald-green)_22%,transparent),color-mix(in_srgb,var(--brand-plum)_24%,transparent)_58%,color-mix(in_srgb,var(--brand-yellow)_26%,transparent))]"
          >
            <HomeCard.Icon>
              <Icons.routines className="size-5 text-foreground-subtle" />
            </HomeCard.Icon>
            <HomeCard.Content>
              <HomeCard.Title>Routines</HomeCard.Title>
              <HomeCard.Description>
                Review the recurring work Littlebird runs for you
              </HomeCard.Description>
            </HomeCard.Content>
          </HomeCard.Root>
        </Grid>
      </YStack>
    </YStack>
  );
});

type ChannelConnectionProps = {
  channelType: MessagingChannelType;
  openLabel?: ReactNode;
  onConnected: () => void;
};

export const ChannelConnectButton = ({
  channelType,
  openLabel,
  className,
  onConnected,
}: ChannelConnectionProps & {
  className?: string;
}) => {
  const channel = CHANNEL_DEFINITIONS[channelType];
  const {
    identifier,
    isChecking: isConnectionChecking,
    isError: isConnectionError,
    isReady: isConnectionReady,
    refetch: refetchConnection,
  } = useChannelConnectionState(channelType);
  const handleConnected = useCallback(() => {
    toast.success(`${channel.name} connected`);
    onConnected();
  }, [channel.name, onConnected]);
  const {
    status: linkCodeStatus,
    openChannel,
    retry: retryLinkCode,
  } = useChannelConnectionSetup({
    channelType,
    identifier,
    isConnectionReady,
    onConnected: handleConnected,
  });

  const buttonClassName = cn('w-full rounded-full', className);

  if (identifier) {
    return (
      <Button
        size="lg"
        variant="outline"
        className={buttonClassName}
        onClick={() =>
          ModalsManager.show(getChannelManageModal(channelType), {
            currentIdentifier: identifier,
          })
        }
      >
        Manage
      </Button>
    );
  }

  if (isConnectionChecking) {
    return (
      <Button
        size="lg"
        className={buttonClassName}
        disabled
        isLoading
        loadingText="Checking…"
      >
        Checking…
      </Button>
    );
  }

  if (isConnectionError) {
    return (
      <Button
        size="lg"
        className={buttonClassName}
        onClick={() => void refetchConnection()}
      >
        Refresh
      </Button>
    );
  }

  if (linkCodeStatus === 'error') {
    return (
      <Button size="lg" className={buttonClassName} onClick={retryLinkCode}>
        Try again
      </Button>
    );
  }

  if (linkCodeStatus === 'expired') {
    return (
      <Button size="lg" className={buttonClassName} onClick={retryLinkCode}>
        Refresh link
      </Button>
    );
  }

  if (linkCodeStatus === 'waiting') {
    return (
      <Button
        size="lg"
        variant="secondary"
        className={buttonClassName}
        onClick={openChannel}
      >
        Open again
      </Button>
    );
  }

  return (
    <Button
      size="lg"
      className={buttonClassName}
      disabled={linkCodeStatus !== 'ready'}
      isLoading={linkCodeStatus === 'preparing'}
      loadingText="Preparing…"
      onClick={openChannel}
    >
      {openLabel ?? channel.openLabel}
    </Button>
  );
};

const ChannelConnectCard = ({
  channelType,
  onConnected,
}: ChannelConnectionProps) => {
  const channel = CHANNEL_DEFINITIONS[channelType];
  const { identifier, conversationLink } =
    useChannelConnectionState(channelType);

  return (
    <YStack
      className={cn(
        CHANNEL_SURFACE_CLASS_NAME,
        'gap-7 rounded-[2rem] p-5 shadow-[inset_0_1px_0_var(--glass-highlight),0_0.25rem_0.75rem_color-mix(in_srgb,var(--glass-shadow)_45%,transparent)]'
      )}
    >
      <XStack className="items-center gap-3.5">
        <ChannelBadge channel={channel} />
        <YStack className="min-w-0 flex-1 gap-0.5">
          <h2 className="text-lg font-medium leading-tight">{channel.name}</h2>
          <p
            className={cn(
              'text-sm text-foreground-subtle',
              identifier && 'truncate'
            )}
          >
            {identifier ?? channel.description}
          </p>
        </YStack>
        {identifier && (
          <button
            type="button"
            aria-label={`Manage ${channel.name}`}
            title={`Manage ${channel.name}`}
            className="flex size-10 shrink-0 items-center justify-center rounded-full text-foreground-muted hover:bg-surface-active hover:text-foreground focus-ring"
            onClick={() =>
              ModalsManager.show(getChannelManageModal(channelType), {
                currentIdentifier: identifier,
              })
            }
          >
            <Icons.cog className="size-5" />
          </button>
        )}
      </XStack>

      {identifier && conversationLink && (
        <button
          type="button"
          className="flex h-11 w-full items-center justify-center rounded-full bg-secondary px-6 text-[15px] font-medium text-foreground hover:bg-surface-active focus-ring"
          onClick={() => handleExternalUrlClick(conversationLink)}
        >
          Open {channel.name}
        </button>
      )}
      {!identifier && (
        <ChannelConnectButton
          channelType={channelType}
          onConnected={onConnected}
        />
      )}
    </YStack>
  );
};

export const ChannelConnect = () => {
  const navigate = useNavigate();
  useChannelConnections(true);
  const handleConnected = useCallback(
    () => navigate({ to: '/channels', replace: true }),
    [navigate]
  );

  return (
    <ChannelRouteSheet name="channel-connect" ariaLabel="Set up messaging">
      <YStack className="gap-6">
        <DashboardBackLink />
        <ChannelPageHeader
          title="Set up messaging"
          description="Connect an app to your Littlebird account"
        />

        <YStack className="gap-4">
          {MESSAGING_CHANNEL_TYPES.map((channelType) => (
            <ChannelConnectCard
              key={channelType}
              channelType={channelType}
              onConnected={handleConnected}
            />
          ))}
        </YStack>
      </YStack>
    </ChannelRouteSheet>
  );
};

const ChannelIntegrationCard = ({
  icon,
  name,
  description,
  isConnected,
  actionLabel,
  actionDisabled = false,
  isActionPending = false,
  actionLoadingText,
  onAction,
  onManage,
  isDesktopOnly = false,
}: {
  icon: ReactNode;
  name: string;
  description: string;
  isConnected: boolean;
  actionLabel?: string;
  actionDisabled?: boolean;
  isActionPending?: boolean;
  actionLoadingText?: string;
  onAction: () => void;
  onManage?: () => void;
  isDesktopOnly?: boolean;
}) => (
  <YStack
    className={cn(
      CHANNEL_SURFACE_CLASS_NAME,
      'min-h-60 justify-between gap-4 rounded-[2rem] p-4 shadow-[inset_0_1px_0_var(--glass-highlight),0_0.25rem_0.75rem_color-mix(in_srgb,var(--glass-shadow)_45%,transparent)] sm:p-5'
    )}
  >
    <YStack className="gap-5">
      <XStack className="items-start justify-between gap-3">
        <XStack
          center
          className="size-12 shrink-0 rounded-2xl bg-white text-black shadow-sm"
        >
          {icon}
        </XStack>
        {isDesktopOnly ? (
          <span className="rounded-full border border-[color-mix(in_srgb,var(--brand-jordy-blue)_45%,transparent)] bg-[color-mix(in_srgb,var(--brand-jordy-blue)_22%,var(--background))] px-2.5 py-1 text-xs font-medium text-[color-mix(in_srgb,var(--brand-jordy-blue)_50%,var(--foreground-default))]">
            Desktop only
          </span>
        ) : isConnected ? (
          <span
            className="mr-1.5 mt-2 size-2.5 shrink-0 rounded-full bg-[var(--brand-emerald-green)] ring-4 ring-[color-mix(in_srgb,var(--brand-emerald-green)_18%,transparent)]"
            aria-label="Connected"
            title="Connected"
          />
        ) : null}
      </XStack>

      <YStack className="min-w-0 gap-1">
        <h2 className="truncate text-lg font-medium">{name}</h2>
        <p className="line-clamp-2 text-sm leading-relaxed text-foreground-muted sm:text-base">
          {description}
        </p>
      </YStack>
    </YStack>

    {isDesktopOnly && actionLabel ? (
      <Button
        variant="secondary"
        size="lg"
        textSize="base"
        className="w-full rounded-full border border-[color-mix(in_srgb,var(--foreground-muted)_16%,transparent)] bg-secondary shadow-none hover:enabled:bg-secondary-hover"
        disabled={actionDisabled}
        isLoading={isActionPending}
        loadingText={actionLoadingText}
        onClick={onAction}
      >
        {actionLabel}
      </Button>
    ) : isConnected && onManage ? (
      <Button
        variant="secondary"
        size="lg"
        textSize="base"
        className="w-full rounded-full border border-[color-mix(in_srgb,var(--foreground-muted)_16%,transparent)] bg-secondary shadow-none hover:enabled:bg-secondary-hover"
        isLoading={isActionPending}
        onClick={onManage}
      >
        Manage
      </Button>
    ) : actionLabel ? (
      <Button
        variant={actionLabel === 'Connect' ? 'secondary' : 'default'}
        size="lg"
        textSize="base"
        className={cn(
          'w-full rounded-full',
          actionLabel === 'Connect' &&
            'border border-[color-mix(in_srgb,var(--foreground-muted)_16%,transparent)] bg-secondary shadow-none hover:enabled:bg-secondary-hover'
        )}
        disabled={actionDisabled}
        isLoading={isActionPending}
        loadingText={actionLoadingText}
        onClick={onAction}
      >
        {actionLabel}
      </Button>
    ) : null}
  </YStack>
);

const IntegrationArtwork = ({
  integrationId,
  iconUrl,
}: {
  integrationId: string;
  iconUrl?: string | null;
}) => {
  const [isImageUnavailable, setIsImageUnavailable] = useState(false);
  const BrandIcon = getBrandIcon(integrationId);

  if (!iconUrl || isImageUnavailable) {
    return BrandIcon ? (
      <BrandIcon className="size-6" />
    ) : (
      <Icons.plugZap className="size-5 text-black/55" />
    );
  }

  return (
    <img
      src={iconUrl}
      alt=""
      className="size-7 rounded-md object-contain"
      onError={() => setIsImageUnavailable(true)}
      onLoad={({ currentTarget }) => {
        if (
          currentTarget.naturalWidth < 24 ||
          currentTarget.naturalHeight < 24
        ) {
          setIsImageUnavailable(true);
        }
      }}
    />
  );
};

const IntegrationCard = observer(
  ({
    integration,
    onConnect,
    onDisconnect,
    onGetDesktop,
    onUpgrade,
  }: {
    integration: IntegrationState;
    onConnect: (integration: IntegrationState) => void;
    onDisconnect: (integration: IntegrationState) => void;
    onGetDesktop: () => void;
    onUpgrade: () => void;
  }) => {
    const toolkitName = integration.info.toolkit_name;
    const isPending = integrationsStore.isPending(toolkitName);
    const isConnectableDeviceIntegration =
      Boolean(integration.info.on_device) &&
      isConnectableDeviceToolkit(toolkitName);
    const isDesktopOnlyIntegration =
      Boolean(integration.info.on_device) ||
      Boolean(integration.info.platforms?.length);
    const requiresDesktop = isDesktopOnlyIntegration && !inDesktop();
    const requiresSubscription =
      !requiresDesktop && integration.info.requires_paid_plan;
    const isSubscriptionPending =
      !integration.isConnected &&
      requiresSubscription &&
      subscriptionStore.state.isLoading;
    const requiresUpgrade =
      !integration.isConnected &&
      requiresSubscription &&
      !isSubscriptionPending &&
      !hasIntegrationAccess();

    return (
      <ChannelIntegrationCard
        icon={<IntegrationArtwork integrationId={toolkitName} />}
        name={integration.info.display_name}
        description={integration.info.description}
        isConnected={integration.isConnected}
        isDesktopOnly={requiresDesktop}
        actionLabel={
          requiresDesktop ? 'Get app' : requiresUpgrade ? 'Upgrade' : 'Connect'
        }
        isActionPending={isPending || isSubscriptionPending}
        actionLoadingText={
          isSubscriptionPending
            ? 'Checking…'
            : integration.isConnected
              ? 'Disconnecting…'
              : 'Connecting…'
        }
        onAction={
          requiresDesktop
            ? onGetDesktop
            : requiresUpgrade
              ? onUpgrade
              : () => onConnect(integration)
        }
        onManage={
          integration.isConnected
            ? isConnectableDeviceIntegration
              ? () => onDisconnect(integration)
              : () =>
                  void confirmIntegrationDisconnect(
                    integration.info.display_name,
                    () => onDisconnect(integration)
                  )
            : undefined
        }
      />
    );
  }
);

const CatalogIntegrationCard = observer(
  ({
    catalogId,
    name,
    description,
    iconUrl,
    isConnected,
    onAction,
    onManage,
    isManagePending = false,
    actionLabel,
    isDesktopOnly = false,
    requiresPaidPlan = true,
  }: {
    catalogId: string;
    name: string;
    description: string;
    iconUrl?: string | null;
    isConnected: boolean;
    onAction: () => void;
    onManage?: () => void;
    isManagePending?: boolean;
    actionLabel?: string;
    isDesktopOnly?: boolean;
    requiresPaidPlan?: boolean;
  }) => {
    const isSubscriptionPending =
      !actionLabel &&
      !isConnected &&
      requiresPaidPlan &&
      subscriptionStore.state.isLoading;
    const requiresUpgrade =
      !actionLabel &&
      !isConnected &&
      requiresPaidPlan &&
      !isSubscriptionPending &&
      !hasIntegrationAccess();

    return (
      <ChannelIntegrationCard
        icon={
          <IntegrationArtwork integrationId={catalogId} iconUrl={iconUrl} />
        }
        name={name}
        description={description}
        isConnected={isConnected}
        isDesktopOnly={isDesktopOnly}
        actionLabel={actionLabel ?? (requiresUpgrade ? 'Upgrade' : 'Connect')}
        isActionPending={isSubscriptionPending || isManagePending}
        actionLoadingText="Checking…"
        onAction={onAction}
        onManage={onManage}
      />
    );
  }
);

const INTEGRATION_SKELETON_SHIMMER_CLASS_NAME = cn(
  'bg-fixed bg-[length:100%_200vh] opacity-[0.16]',
  'bg-[linear-gradient(to_bottom,var(--foreground-muted)_0%,var(--foreground-muted)_40%,var(--foreground-subtle)_50%,var(--foreground-muted)_60%,var(--foreground-muted)_100%)]',
  'animate-shimmer-skeleton-vertical'
);

const IntegrationGridSkeleton = () => (
  <Grid
    className="grid-cols-2 gap-5"
    aria-label="Loading integrations"
    aria-busy="true"
  >
    {Array.from({ length: 6 }, (_, index) => (
      <YStack
        key={index}
        className="min-h-60 justify-between gap-4 rounded-[2rem] border border-[var(--glass-edge-strong)] bg-secondary p-4 sm:p-5"
      >
        <YStack className="gap-5">
          <Box
            className={cn(
              'size-12 rounded-2xl',
              INTEGRATION_SKELETON_SHIMMER_CLASS_NAME
            )}
          />
          <YStack className="gap-3">
            <Box
              className={cn(
                'h-5 w-3/4 rounded-full',
                INTEGRATION_SKELETON_SHIMMER_CLASS_NAME
              )}
            />
            <YStack className="gap-2">
              <Box
                className={cn(
                  'h-3.5 w-full rounded-full',
                  INTEGRATION_SKELETON_SHIMMER_CLASS_NAME
                )}
              />
              <Box
                className={cn(
                  'h-3.5 w-5/6 rounded-full',
                  INTEGRATION_SKELETON_SHIMMER_CLASS_NAME
                )}
              />
            </YStack>
          </YStack>
        </YStack>
        <Box
          className={cn(
            'h-11 w-full rounded-full',
            INTEGRATION_SKELETON_SHIMMER_CLASS_NAME
          )}
        />
      </YStack>
    ))}
  </Grid>
);

const IntegrationLoadError = ({
  title,
  onRefresh,
  fillAvailable = false,
}: {
  title: string;
  onRefresh: () => void;
  fillAvailable?: boolean;
}) => (
  <YStack
    center
    className={cn(
      'gap-4 text-center',
      fillAvailable && 'min-h-64 flex-1 justify-center'
    )}
  >
    <XStack
      center
      className="size-10 rounded-full bg-background-muted text-foreground-subtle"
    >
      <Icons.warnTriangle className="size-4" />
    </XStack>
    <p className="font-medium text-foreground">{title}</p>
    <Button className="min-w-32 rounded-full px-6" onClick={onRefresh}>
      Refresh
    </Button>
  </YStack>
);

const IntegrationFilters = ({
  selection,
  hasSuggested,
  categories,
  onSelectionChange,
}: {
  selection: Selection;
  hasSuggested: boolean;
  categories: Array<{ slug: string; label: string; icon: IconName }>;
  onSelectionChange: (selection: Selection) => void;
}) => (
  <ScrollMask
    direction="horizontal"
    showEndFade
    fadeSize={24}
    className="scrollbar-hide"
  >
    <XStack
      className="w-max min-w-full gap-3 px-1 pb-1"
      aria-label="Integration categories"
    >
      <Button
        size="lg"
        textSize="base"
        variant={selection === 'all' ? 'default' : 'ghost'}
        className={cn(
          'shrink-0 rounded-full border border-transparent pl-3 pr-4 transition-colors',
          selection !== 'all' && SOFT_INSET_CLASS_NAME
        )}
        onClick={() => onSelectionChange('all')}
      >
        <Icons.gridPlus className="size-4" />
        All
      </Button>
      {hasSuggested ? (
        <Button
          size="lg"
          textSize="base"
          variant={selection === SUGGESTED_SELECTION ? 'default' : 'ghost'}
          className={cn(
            'shrink-0 rounded-full border border-transparent pl-3 pr-4 transition-colors',
            selection !== SUGGESTED_SELECTION && SOFT_INSET_CLASS_NAME
          )}
          onClick={() => onSelectionChange(SUGGESTED_SELECTION)}
        >
          <Icons.sparkles className="size-4" />
          Suggested
        </Button>
      ) : null}
      {categories.map((category) => {
        const CategoryIcon = Icons[category.icon];
        return (
          <Button
            key={category.slug}
            size="lg"
            textSize="base"
            variant={selection === category.slug ? 'default' : 'ghost'}
            className={cn(
              'shrink-0 rounded-full border border-transparent pl-3 pr-4 transition-colors',
              selection !== category.slug && SOFT_INSET_CLASS_NAME
            )}
            onClick={() => onSelectionChange(category.slug)}
          >
            <CategoryIcon className="size-4" />
            {category.label}
          </Button>
        );
      })}
    </XStack>
  </ScrollMask>
);

export const ChannelIntegrations = observer(() => {
  // Electron keeps the default little-bird:// CTA, which just refocuses the app
  const returnTo = inBrowser()
    ? { path: '/channels/integrations', channel: readViaChannel() }
    : undefined;
  const { handleAction } = useIntegrationActions('settings', { returnTo });
  const deleteMCPServerMutation = useDeleteMCPServerMutation();
  const { setHash } = useHashRoute();
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Selection>('all');
  const {
    isLoading,
    loadError,
    mcpLoadError,
    retryLoad,
    retryMCP,
    allItems,
    visibleItems,
    presentCategories,
    effectiveSelection,
    hasSuggested,
  } = useIntegrationGrid({
    search,
    filter: 'all',
    selection,
    includeDesktopOnly: true,
  });
  useConnectFromLink(allItems, isLoading, returnTo);
  const channelVisibleItems = visibleItems.filter(
    (item) =>
      item.type !== 'integration' ||
      Boolean(item.integration.info.description.trim())
  );
  const hasActiveFilters =
    search.trim().length > 0 || effectiveSelection !== 'all';
  const openUpgrade = (source: string) => {
    trackEvent('UPGRADE_BUTTON_CLICKED', { source });
    setHash('settings/Subscription');
  };
  const openUpgradeWithoutTracking = () => setHash('settings/Subscription');
  const openDesktopDownloadPage = () => handleExternalUrlClick(DOWNLOAD_URL);
  const disconnectMCPServer = async (serverId: string, name: string) => {
    await confirmIntegrationDisconnect(name, async () => {
      try {
        await deleteMCPServerMutation.mutateAsync(serverId);
        toast.success(`${name} disconnected successfully`);
      } catch {
        toast.error(`Failed to disconnect ${name}`);
      }
    });
  };

  return (
    <ChannelRouteSheet name="channel-integrations" ariaLabel="Integrations">
      <YStack className="min-h-full gap-8 pb-8 sm:pb-12">
        <DashboardBackLink />
        <YStack className="min-h-0 flex-1 gap-5">
          <h1 className="text-3xl font-light tracking-[-0.03em] text-foreground sm:text-4xl">
            Integrations
          </h1>
          <YStack className="gap-5">
            <ChannelSearchInput
              value={search}
              label="Search integrations"
              placeholder="Search integrations"
              onChange={setSearch}
            />
            <IntegrationFilters
              selection={effectiveSelection}
              hasSuggested={hasSuggested}
              categories={presentCategories}
              onSelectionChange={setSelection}
            />
          </YStack>

          {loadError ? (
            <IntegrationLoadError
              title="Couldn’t load integrations"
              onRefresh={retryLoad}
              fillAvailable
            />
          ) : isLoading ? (
            <IntegrationGridSkeleton />
          ) : (
            <YStack className="gap-4">
              {mcpLoadError ? (
                <IntegrationLoadError
                  title="Some integrations couldn’t load"
                  onRefresh={retryMCP}
                />
              ) : null}

              {channelVisibleItems.length > 0 ? (
                <Grid className="grid-cols-2 gap-5">
                  {channelVisibleItems.map((item) => {
                    if (item.type === 'integration') {
                      const toolkitName = item.integration.info.toolkit_name;
                      return (
                        <IntegrationCard
                          key={toolkitName}
                          integration={item.integration}
                          onGetDesktop={openDesktopDownloadPage}
                          onConnect={(selected) =>
                            void handleAction(selected, 'connect')
                          }
                          onDisconnect={(selected) =>
                            void handleAction(selected, 'disconnect')
                          }
                          onUpgrade={() =>
                            openUpgrade(`channel_dashboard:${toolkitName}`)
                          }
                        />
                      );
                    }

                    if (item.type === 'mcp_catalog') {
                      return (
                        <CatalogIntegrationCard
                          key={item.entry.id}
                          catalogId={item.entry.id}
                          name={item.entry.name}
                          description={item.entry.description}
                          iconUrl={item.entry.icon_url}
                          isConnected={false}
                          requiresPaidPlan={item.entry.requires_paid_plan}
                          onAction={() =>
                            void connectIntegration(
                              {
                                kind: 'mcp',
                                catalogId: item.entry.id,
                                displayName: item.entry.name,
                                url: item.entry.url,
                                requiresPaidPlan: item.entry.requires_paid_plan,
                              },
                              `channel_dashboard:${item.entry.id}`,
                              {
                                onUpgrade: openUpgradeWithoutTracking,
                                returnTo,
                              }
                            )
                          }
                        />
                      );
                    }

                    if (item.type === 'local_mcp_template') {
                      return (
                        <CatalogIntegrationCard
                          key={item.entry.id}
                          catalogId={item.entry.id}
                          name={item.entry.name}
                          description={item.entry.description}
                          iconUrl={item.entry.icon_url}
                          isConnected={false}
                          isDesktopOnly
                          actionLabel="Get app"
                          onAction={openDesktopDownloadPage}
                        />
                      );
                    }

                    if (item.type === 'bundled_mcp_template') {
                      return (
                        <CatalogIntegrationCard
                          key={item.template.id}
                          catalogId={item.template.id}
                          name={item.template.name}
                          description={item.template.description}
                          isConnected={false}
                          isDesktopOnly
                          actionLabel="Get app"
                          onAction={openDesktopDownloadPage}
                        />
                      );
                    }

                    if (item.type !== 'mcp_server') return null;

                    return (
                      <CatalogIntegrationCard
                        key={item.server.id}
                        catalogId={item.server.id}
                        name={item.server.name}
                        description={item.description ?? item.server.url}
                        iconUrl={item.iconUrl}
                        isConnected={item.isConnected}
                        isManagePending={
                          deleteMCPServerMutation.isPending &&
                          deleteMCPServerMutation.variables === item.server.id
                        }
                        onAction={() =>
                          void connectIntegration(
                            {
                              kind: 'mcp',
                              catalogId: item.server.id,
                              displayName: item.server.name,
                              url: item.server.url,
                              requiresPaidPlan: true,
                            },
                            `channel_dashboard:${item.server.id}`,
                            {
                              onUpgrade: openUpgradeWithoutTracking,
                              returnTo,
                            }
                          )
                        }
                        onManage={
                          item.isConnected
                            ? () =>
                                void disconnectMCPServer(
                                  item.server.id,
                                  item.server.name
                                )
                            : undefined
                        }
                      />
                    );
                  })}
                </Grid>
              ) : (
                <YStack
                  className={cn(
                    'gap-2 rounded-[2rem] p-5',
                    CHANNEL_SURFACE_CLASS_NAME
                  )}
                >
                  <p className="font-medium">
                    {hasActiveFilters
                      ? 'No matching integrations'
                      : 'No integrations available yet'}
                  </p>
                  <p className="text-sm text-foreground-subtle">
                    {hasActiveFilters
                      ? 'Try changing or clearing the filters.'
                      : 'Check back soon as more connected apps become available.'}
                  </p>
                </YStack>
              )}
            </YStack>
          )}
        </YStack>
      </YStack>
    </ChannelRouteSheet>
  );
});

export const ChannelSettings = observer(function ChannelSettings() {
  const promptFields = useChatPromptFields();
  const user = userStore.user;
  const accountName = user?.name?.trim();
  const accountLabel = accountName || user?.email || '';
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const saveInstructions = async () => {
    setIsSavingInstructions(true);
    const saved = await promptFields.saveGlobalPrompt();
    setIsSavingInstructions(false);

    if (saved) {
      toast.success('Instructions saved');
    } else {
      toast.error(
        'Couldn’t save instructions. Your previous instructions are unchanged. Try again.'
      );
    }
  };

  return (
    <ChannelRouteSheet name="channel-settings" ariaLabel="Channel settings">
      <YStack className="gap-10 pb-16 sm:pb-20">
        <DashboardBackLink />
        <ChannelPageHeader
          title="Settings"
          description="Manage your preferences and account"
        />

        <YStack className="gap-4">
          <h2 className="text-lg font-medium text-foreground">
            Custom instructions
          </h2>
          <ChannelSurface className="gap-3 p-5">
            <TextArea
              aria-label="Custom instructions"
              className={cn(
                'min-h-36 resize-none text-base leading-relaxed',
                SOFT_INSET_CLASS_NAME
              )}
              placeholder="For example: Keep replies concise and call out anything urgent first."
              value={promptFields.inputGlobalPrompt}
              disabled={isSavingInstructions}
              onChange={(event) =>
                promptFields.setInputGlobalPrompt(event.target.value)
              }
            />
            <XStack className="items-center justify-end gap-2">
              {promptFields.globalPromptDirty ? (
                <Button
                  variant="ghost"
                  disabled={isSavingInstructions}
                  onClick={promptFields.discardGlobalPrompt}
                >
                  Discard
                </Button>
              ) : null}
              <Button
                className="rounded-full px-5"
                disabled={
                  !promptFields.globalPromptDirty || isSavingInstructions
                }
                isLoading={isSavingInstructions}
                loadingText="Saving…"
                onClick={() => void saveInstructions()}
              >
                Save instructions
              </Button>
            </XStack>
          </ChannelSurface>
        </YStack>

        <YStack className="gap-4">
          <h2 className="text-lg font-medium text-foreground">Account</h2>
          <ChannelSurface className="p-6">
            <XStack className="items-center gap-4">
              {user ? (
                <>
                  <UserInitialsAvatar
                    label={accountLabel}
                    className="size-11"
                  />
                  <YStack className="min-w-0 flex-1 gap-0">
                    <p className="truncate font-medium">{accountLabel}</p>
                    {accountName ? (
                      <p className="truncate text-sm text-foreground-subtle">
                        {user.email}
                      </p>
                    ) : null}
                  </YStack>
                </>
              ) : (
                <p className="min-w-0 flex-1 text-sm text-foreground-subtle">
                  Account details unavailable
                </p>
              )}
              <Button
                className="shrink-0 rounded-full px-5"
                isLoading={isSigningOut}
                loadingText="Signing out…"
                onClick={async () => {
                  setIsSigningOut(true);
                  try {
                    await logoutUser('manual');
                  } finally {
                    setIsSigningOut(false);
                  }
                }}
              >
                Sign out
              </Button>
            </XStack>
          </ChannelSurface>
        </YStack>

        <p className="text-center text-sm text-foreground-subtle">
          Channels is in beta — email us at{' '}
          <button
            className="rounded-sm underline underline-offset-2 hover:text-foreground focus-ring"
            onClick={() => openChannelsBetaFeedback('channel_dashboard')}
          >
            {SUPPORT_EMAIL}
          </button>
        </p>
      </YStack>
    </ChannelRouteSheet>
  );
});
